import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {dataToolSchemas} from '../../utils/schemas';

const toolName = 'create-cms-collection' as const;

// Field definition schema for creating fields with the collection
const collectionFieldSchema = z.object({
  displayName: z.string().describe('Display name for the field'),
  type: z
    .string()
    .describe(
      'Field type (e.g., "PlainText", "RichText", "Email", "Number", "Reference", etc.)'
    ),
  isRequired: z
    .boolean()
    .optional()
    .describe('Whether the field is required (default: false)'),
  helpText: z.string().optional().describe('Help text for the field'),
  metadata: z
    .object({
      collectionId: dataToolSchemas.collectionId.optional(),
      options: z
        .array(
          z.object({
            name: z.string(),
          })
        )
        .optional()
        .describe('Required for Option fields'),
    })
    .optional()
    .describe(
      'Additional metadata needed for reference fields or option fields'
    ),
});

const argumentZod = z.object({
  displayName: z
    .string()
    .min(1)
    .describe('The display name of the collection (e.g., "Blog Posts")'),
  singularName: z
    .string()
    .min(1)
    .describe(
      'The singular name for items in this collection (e.g., "Blog Post"). If not provided by the user, the agent should just concatenate the displayName with " item" suffix (e.g. "Blog Posts item")'
    ),
  slug: z
    .string()
    .optional()
    .describe(
      'URL slug for the collection (e.g., "blog-posts"). If not provided, will be auto-generated from displayName as kebab-case'
    ),
  fields: z
    .array(collectionFieldSchema)
    .optional()
    .describe(
      'Optional array of fields to create with the collection. If not provided, collection will be created with default name field only'
    ),
});

export const createCMSCollectionManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Create CMS collection',
  family: 'cms',
  llmTool: {
    name: toolName,
    description: `Creates a new CMS collection with optional custom fields. 
      The collection will be created in staging and requires publishing to go live. 
      A detail page template will also be created automatically. 
      IMPORTANT: If creating a new collection fails because a collection with the same name already exists, you need to fetch the details of the pre-existing collection, then stop trying to execute tools, and immediately inform the user of the conflict and the details of the pre-existing collection.`,
    inputSchema: argumentZod,
  },
  type: 'server',
  // Site-scoped: resource doesn't exist yet, no per-collection FGAC needed
  permission: [{resource: 'SiteCollection', action: 'manage'}],
  confirmBeforeExecuting: false,
});

export type CreateCMSCollectionArgumentType = z.infer<typeof argumentZod>;
